import { DateFormats, formatDate } from '@strata/helper-functions';
import { Button } from '@strata/react-buttons';
import { DateInputFormik } from '@strata/react-date-time';
import { FormAccordion } from '@strata/react-form-section';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { TextInputFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { submitBenefitCheckRequest } from 'api/scopeAssessorAPI';
import { EligibilityDialog } from 'components/referralForm/dialogs/EligibilityDialog';
import { useFormResetRef } from 'components/referralForm/FormHelpers';
import { handleError } from 'services/Error';
import { useGetPayerSegmentsQuery } from 'store/api/referenceApi';
import { fetchPrescriberInfo } from 'store/referralForm/referenceSlice';
import { nextSection, resetForm, updateCapturedData } from 'store/referralForm/referralFormSlice';

export const PrescriberAndPatient = ({ section, isOpen, width }) => {
    const dispatch = useDispatch();
    const { capturedData, activeSection } = useSelector((state) => state.referralForm);

    const formRef = useFormResetRef(isOpen);

    const [showEligibilityDialog, setShowEligibilityDialog] = useState(false);
    const [showLoading, setShowLoading] = useState(false);
    const [benefitInfo, setBenefitInfo] = useState();

    const { prescriberList } = useSelector((state) => state.reference);
    const { data } = useGetPayerSegmentsQuery();
    const { payerSegmentList, payerSegmentMap } = data ?? {};

    useEffect(() => {
        if (!prescriberList) {
            dispatch(fetchPrescriberInfo());
        }
    }, [prescriberList, dispatch]);

    // Focus first input without a single option on active section open. Focus submit button if pending reauth
    const prescriberRef = useRef(null);
    const payerIdRef = useRef(null);
    const patientNameRef = useRef(null);
    const submitBtnRef = useRef(null);
    const { pendingReauth } = capturedData;
    useEffect(() => {
        if (isOpen && activeSection === section) {
            if (pendingReauth) {
                submitBtnRef.current?.focus();
            } else if (prescriberList && payerSegmentList) {
                if (prescriberList.length > 1) {
                    setTimeout(() => prescriberRef.current?.focus(), FormAccordion.AnimateDuration);
                } else if (payerSegmentList.length > 1) {
                    setTimeout(() => payerIdRef.current?.focus(), FormAccordion.AnimateDuration);
                } else {
                    setTimeout(
                        () => patientNameRef.current?.focus(),
                        FormAccordion.AnimateDuration,
                    );
                }
            }
        }
    }, [isOpen, activeSection, section, payerSegmentList, prescriberList, pendingReauth]);

    const checkBenefits = async (values) => {
        try {
            setShowLoading(true);
            setBenefitInfo(
                await submitBenefitCheckRequest({
                    payerId: values.payerId,
                    segmentId: values.segmentId,
                    memberId: values.memberId,
                    dateOfBirth: formatDate(values.memberDOB, DateFormats.HYPHEN_DATE),
                    firstName: values.firstName?.toUpperCase(),
                }),
            );
            setShowEligibilityDialog(true);
        } catch (err) {
            handleError('Error processing benefit check.', err);
        } finally {
            setShowLoading(false);
        }
    };

    const onCloseEligibilityDialog = () => {
        setShowEligibilityDialog(false);
        prescriberRef.current?.focus();
    };

    const onPatientConfirmation = ({ values, handleReset }) => {
        setShowEligibilityDialog(false);
        if (benefitInfo?.is_eligible) {
            // Eligible patient confirmation
            dispatch(
                updateCapturedData({
                    ...values,
                    referralId: uuidv4(),
                    prescriberName: prescriberList?.find(
                        (prescriber) => prescriber.npi === values.prescriberNpi,
                    ).name,
                    prescriberPhone: prescriberList?.find(
                        (prescriber) => prescriber.npi === values.prescriberNpi,
                    ).phone,
                    practiceNpi: prescriberList?.find(
                        (prescriber) => prescriber.npi === values.prescriberNpi,
                    ).practiceNpi,
                    memberFirstName: benefitInfo.patient_first_name,
                    memberLastName: benefitInfo.patient_last_name,
                    memberAge: benefitInfo.member_age,
                    memberCity: benefitInfo.member_city,
                    memberState: benefitInfo.member_state,
                    memberZip: benefitInfo.member_zip,
                    memberGender: benefitInfo.gender,
                    benefitAttributes: benefitInfo.attributes ?? [],
                    segmentName: payerSegmentMap?.[values.segmentId].segment_name,
                }),
            );
            dispatch(nextSection(section));
        } else {
            // Cancel referral
            dispatch(resetForm());
            handleReset();
            prescriberRef.current?.focus();
        }
    };

    return (
        <Formik
            initialValues={capturedData}
            enableReinitialize
            validationSchema={Yup.object({
                prescriberNpi: Yup.string().required(validationStrings.requiredField.errorMessage),
                segmentId: Yup.string().required(validationStrings.requiredField.errorMessage),
                firstName: Yup.string().required(validationStrings.requiredField.errorMessage),
                memberId: Yup.string()
                    .required(validationStrings.requiredField.errorMessage)
                    .min(5, 'ID must be at least 5 characters.')
                    .max(15, 'ID cannot be more than 15 characters.'),
                memberDOB: DateInputFormik.validator()
                    .required(validationStrings.requiredField.errorMessage)
                    .max(dayjs().endOf('day'), 'Date of birth cannot be in the future.'),
            })}
            onSubmit={checkBenefits}
            innerRef={formRef}
        >
            {(formik) => (
                <Form autoComplete="off" style={{ width }}>
                    <FullscreenLoading
                        isShowing={showLoading}
                        loadingText="Checking patient benefits and eligibility..."
                    />
                    <EligibilityDialog
                        isOpen={showEligibilityDialog}
                        benefitInfo={benefitInfo}
                        onSecondaryButtonClick={onCloseEligibilityDialog}
                        onPrimaryButtonClick={() => onPatientConfirmation(formik)}
                    />
                    <SuggestSingleSelectFormik
                        id="prescriberNpi"
                        label="prescriber"
                        required
                        inputRef={prescriberRef}
                        isLoading={!prescriberList}
                        values={prescriberList}
                        onChange={(newValue) =>
                            formik.setFieldValue('prescriberNpi', newValue?.npi)
                        }
                        selectedItem={prescriberList?.find(
                            (prescriber) => prescriber?.npi === formik.values.prescriberNpi,
                        )}
                    />
                    <SuggestSingleSelectFormik
                        id="segmentId"
                        label="Health Plan"
                        required
                        inputRef={payerIdRef}
                        isLoading={!payerSegmentList}
                        values={payerSegmentList}
                        onChange={async (newValue) => {
                            await formik.setFieldValue('segmentId', newValue?.segment_id);
                            await formik.setFieldValue('payerId', newValue?.payer_id);
                        }}
                        selectedItem={payerSegmentMap?.[formik.values.segmentId]}
                    />
                    <TextInputFormik
                        label="Patient First Name"
                        id="firstName"
                        required
                        inputRef={patientNameRef}
                    />
                    <TextInputFormik label="Cardholder Id" id="memberId" required />
                    <DateInputFormik
                        label="Patient Date of Birth"
                        id="memberDOB"
                        required
                        hideCalendarPopover
                    />
                    <Button
                        intent={Button.Intent.PRIMARY}
                        label="Check Patient Benefits"
                        type="submit"
                        ref={submitBtnRef}
                        disabled={!(formik.isValid && (formik.dirty || pendingReauth))}
                    />
                </Form>
            )}
        </Formik>
    );
};

PrescriberAndPatient.propTypes = {
    section: PropTypes.number.isRequired,
    isOpen: PropTypes.bool.isRequired,
    width: PropTypes.string.isRequired,
};
